import type { FormEvent } from 'react';
import { forwardRef, useImperativeHandle, useState } from 'react';

import { useCoreDispatch } from '../../../store/CoreStore';
import { useAutoplaySlice } from '../../../store/slices/autoplay/selectors';
import { autoplayActions } from '../../../store/slices/autoplay/slice';
import { AutoplayLimits } from '../../../store/slices/autoplay/types';
import { useBetSlice } from '../../../store/slices/bets/selectors';
import { useConfigSlice } from '../../../store/slices/config/selectors';
import { useGameStateSlice } from '../../../store/slices/game/selectors';
import { CurrencyConnected } from '../../currency/CurrencyConnected';
import { GameRangeInput } from '../../game/gameRangeInput';
import { L12nText } from '../../text/L12nText';

import styles from './AutoplayLimitsConnected.module.css';

export interface AutoPlayLimitRef {
  onSubmit: (event: FormEvent<HTMLFormElement>) => boolean;
}

const MIN_BET_LIMIT_MULTIPLIER = 10000;

export const AutoPlayLimitInputsConnected = forwardRef<AutoPlayLimitRef>((props, ref) => {
  const dispatch = useCoreDispatch();
  const balance = useGameStateSlice.getDeductibleBalance();
  const limitConfigs = useConfigSlice.compliance()?.extendedAutoplay;
  const currentLimitValues = useAutoplaySlice.getAutoplayLimits();
  const decimals = useGameStateSlice.getCurrencyInfo()?.digitsNumber || 2;

  const [decreaseLimit, setDecreaseLimit] = useState(currentLimitValues?.balanceDecreaseLimit || 0);
  const [increaseLimit, setIncreaseLimit] = useState(currentLimitValues?.balanceIncreaseLimit || 0);
  const [winLimit, setWinLimit] = useState(currentLimitValues?.singleWinLimit || 0);
  const minBet = useBetSlice.getMinBet();

  const [requiredError, setRequiredError] = useState(false);

  useImperativeHandle(ref, () => ({
    onSubmit(): boolean {
      if (limitConfigs) {
        const limits: AutoplayLimits = {
          singleWinLimit: winLimit || null,
          balanceIncreaseLimit: increaseLimit || null,
          balanceDecreaseLimit: decreaseLimit || null,
        };
        if (!decreaseLimit) {
          setRequiredError(true);
          return false;
        }
        dispatch(autoplayActions.setLimits(limits));
      }

      return true;
    },
  }));

  if (!limitConfigs) return null;

  const sliderStep = Math.pow(1 / 10, decimals);

  return (
    <>
      <p style={{ marginTop: 0 }}>
        <L12nText tk="STOP_DECREASES" />*
        <br />
        <span className={styles.controlStateValue}>
          {decreaseLimit > 0 && !requiredError ? (
            <>
              (<CurrencyConnected value={decreaseLimit} />)
            </>
          ) : (
            <>
              (<L12nText tk="REQUIRED" />)
            </>
          )}
        </span>
        <br />
      </p>

      <GameRangeInput
        value={decreaseLimit}
        onChange={(val): void => {
          setDecreaseLimit(val);
          setRequiredError(false);
        }}
        onDrag={(val): void => {
          setDecreaseLimit(val);
        }}
        min={0}
        max={balance > 0 ? balance : 100}
        step={sliderStep}
      />

      <p>
        <L12nText tk="STOP_INCREASES" />
        <br />
        <span className={styles.controlStateValue}>
          {increaseLimit > 0 ? (
            <>
              (<CurrencyConnected value={increaseLimit} />)
            </>
          ) : (
            <>
              (<L12nText tk="OFF" />)
            </>
          )}
        </span>
      </p>

      <GameRangeInput
        value={increaseLimit}
        onChange={(val): void => {
          setIncreaseLimit(val);
        }}
        onDrag={(val): void => {
          setIncreaseLimit(val);
        }}
        min={0}
        max={MIN_BET_LIMIT_MULTIPLIER * minBet}
        step={sliderStep}
      />

      <p>
        <L12nText tk="STOP_SINGLE_EXCEEDS" />
        <br />
        <span className={styles.controlStateValue}>
          {winLimit > 0 ? (
            <>
              (<CurrencyConnected value={winLimit} />)
            </>
          ) : (
            <>
              (<L12nText tk="OFF" />)
            </>
          )}
        </span>
      </p>
      <GameRangeInput
        value={winLimit}
        onChange={(val): void => {
          setWinLimit(val);
        }}
        onDrag={(val): void => {
          setWinLimit(val);
        }}
        min={0}
        max={MIN_BET_LIMIT_MULTIPLIER * minBet}
        step={sliderStep}
      />
    </>
  );
});

AutoPlayLimitInputsConnected.displayName = 'AutoPlayLimitInputsConnected';
